import * as React from "react";

import { cn } from "src/lib/utils";

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(({ className, ...props }, ref) => (
	<div className="tw-relative tw-w-full tw-overflow-x-auto">
		<table
			ref={ref}
			// border-collapse is load-bearing: the row separators live on <tr>, and row borders
			// are not painted in the default separate-borders mode.
			className={cn("tw-w-full tw-caption-bottom tw-border-collapse tw-text-[14px]", className)}
			{...props}
		/>
	</div>
));
Table.displayName = "Table";

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
	({ className, ...props }, ref) => <thead ref={ref} className={cn("[&_tr]:tw-border-b [&_tr]:tw-border-border", className)} {...props} />
);
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
	({ className, ...props }, ref) => <tbody ref={ref} className={cn("[&_tr:last-child]:tw-border-0", className)} {...props} />
);
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
	({ className, ...props }, ref) => (
		<tfoot ref={ref} className={cn("tw-border-t tw-border-border tw-bg-bg tw-font-medium", className)} {...props} />
	)
);
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(({ className, ...props }, ref) => (
	<tr
		ref={ref}
		className={cn("tw-border-b tw-border-border tw-transition-colors hover:tw-bg-bg data-[state=selected]:tw-bg-teal-tint", className)}
		{...props}
	/>
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
	({ className, ...props }, ref) => (
		<th
			ref={ref}
			className={cn(
				"tw-h-11 tw-whitespace-nowrap tw-px-3 tw-text-left tw-align-middle tw-text-[12px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-text-soft [&:has([role=checkbox])]:tw-pr-0",
				className
			)}
			{...props}
		/>
	)
);
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
	({ className, ...props }, ref) => (
		<td ref={ref} className={cn("tw-px-3 tw-py-3.5 tw-align-middle [&:has([role=checkbox])]:tw-pr-0", className)} {...props} />
	)
);
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
	({ className, ...props }, ref) => <caption ref={ref} className={cn("tw-mt-4 tw-text-[13px] tw-text-text-soft", className)} {...props} />
);
TableCaption.displayName = "TableCaption";

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
